/**
 * Mail rules shared by the server and every client (contracts mail.ts, docs/features/mail.md): addresses, subjects,
 * reply recipients and quoting, the tab strip, the rule-based tab fallback when the AI classifier can't answer, and the
 * DNS records a workspace adds to host its domain on Bundle (everything points at bundle.direct).
 */
import type { ContactKind, DnsRecord, MailAddress, MailBodyView, MailLabel, MailMessage, MailNotifyPrefs, MailTab, MailTabDef, MailTabId, MailThread, Mailbox } from "@bundle/contracts";
import { addDays, fromLocal, localParts } from "./calendar.ts";
import { fold } from "./search.ts";

/** Tabs in display order (Gmail: Primary, Promotions, Social, Updates; ours: uncategorised, Clients, Team, Promotions, Updates). */
export const MAIL_TABS: readonly MailTab[] = ["email", "clients", "team", "promotions", "updates"];

/** The classifier's answer is used only at or above this confidence; below it the thread stays in Email. */
export const MIN_TAB_CONFIDENCE = 0.6;

/** Longest snippet shown in a thread row. */
export const SNIPPET_LENGTH = 140;

/** Bundle's mail host names (MX targets, SPF include, DKIM/DMARC/return-path record parents). */
export const MAIL_HOST = {
  mx: ["mx1.bundle.direct", "mx2.bundle.direct"],
  spf: "_spf.bundle.direct",
  dkim: "dkim.bundle.direct",
  returnPath: "bounce.bundle.direct",
  dmarc: "bundle.direct",
  mtaSts: "mta-sts.bundle.direct",
  /** Mail-app setup discovery (Thunderbird autoconfig, Outlook autodiscover), served by the API Worker. */
  autoconfig: "autoconfig.bundle.direct",
  autodiscover: "autodiscover.bundle.direct",
} as const;

/** The DKIM selector customers publish (`bundle1._domainkey`); rotation moves to bundle2. */
export const DKIM_SELECTOR = "bundle1";

// ---------------------------------------------------------------------------------------------------------------
// Addresses

const LOCAL_PART = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/;

/**
 * The API path of a sender's picture (BIMI logo, Gravatar or site icon, cached by the server), for an Avatar's `src`;
 * undefined for an address that isn't valid. Web adds the file read token like for files.
 */
export function mailAvatarPath(ws: string, address: string): string | undefined {
  const a = normalizeAddress(address);
  return a === undefined ? undefined : `/v1/w/${encodeURIComponent(ws)}/mail/avatars/${encodeURIComponent(a)}`;
}

/** Lower case, trimmed address, or undefined when it isn't a plain `local@domain` (no quoted local parts). */
export function normalizeAddress(raw: string): string | undefined {
  const a = raw.trim().replace(/^<|>$/g, "").toLowerCase();
  const at = a.lastIndexOf("@");
  if (at <= 0 || at === a.length - 1 || a.length > 320) return undefined;
  const local = a.slice(0, at);
  const domain = a.slice(at + 1);
  if (local.length > 64 || !LOCAL_PART.test(local) || !DOMAIN.test(domain)) return undefined;
  return a;
}

/** A valid mailbox name for `localPart@domain` (what admins type when creating one). */
export function isValidLocalPart(localPart: string): boolean {
  const l = localPart.trim().toLowerCase();
  return l.length > 0 && l.length <= 64 && LOCAL_PART.test(l);
}

/**
 * The local part of a person's own mailbox, from their sign-in email (Settings → Email "Create you@domain", and the
 * personal mailboxes made for members): the part before "@", lower case, anything but letters, digits, ".", "_" and "-"
 * dropped ("Ana.Souza+x@gmail.com" → "ana.souzax"). "" without an email (then nothing is offered).
 */
export function ownLocalPart(email: string | undefined): string {
  return (email?.split("@")[0] ?? "").toLowerCase().replace(/[^a-z0-9._-]/g, "");
}

/** The domain of an address (lower case), or "" when there is none. */
export function addressDomain(address: string): string {
  const at = address.lastIndexOf("@");
  return at < 0 ? "" : address.slice(at + 1).trim().toLowerCase();
}

/** `Ana Souza <ana@x.com>`, `"Souza, Ana" <ana@x.com>`, `<ana@x.com>` or `ana@x.com` → a MailAddress. */
export function parseAddress(raw: string): MailAddress | undefined {
  const s = raw.trim();
  const m = /^(.*?)\s*<([^<>]+)>$/.exec(s);
  if (m === null) {
    const address = normalizeAddress(s);
    return address === undefined ? undefined : { address };
  }
  const address = normalizeAddress(m[2]!);
  if (address === undefined) return undefined;
  const name = m[1]!.trim().replace(/^["“](.*)["”]$/, "$1").replace(/\\(.)/g, "$1").trim();
  return name === "" || name.toLowerCase() === address ? { address } : { address, name };
}

/** A To/Cc list cut at its commas, semicolons and line breaks: not the ones in a quoted name (`\"` stays quoted, and a phone's “curly quotes” quote too) or in <…>. */
export function splitAddressList(raw: string): string[] {
  const parts: string[] = [];
  let cur = "";
  let quoted = false;
  let curly = false;
  let escaped = false;
  let angle = false;
  for (const ch of raw) {
    if (escaped) escaped = false;
    else if (quoted && ch === "\\") escaped = true;
    else if (ch === '"' || (curly && ch === "”")) {
      quoted = !quoted;
      curly = false;
    } else if (ch === "“" && !quoted) {
      quoted = true;
      curly = true;
    } else if (ch === "<" && !quoted) angle = true;
    else if (ch === ">" && !quoted) angle = false;
    if ((ch === "," || ch === ";" || ch === "\n") && !quoted && !angle) {
      parts.push(cur);
      cur = "";
    } else cur += ch;
  }
  parts.push(cur);
  return parts;
}

/** The entries of a typed list that aren't addresses, trimmed. */
export function invalidAddressParts(raw: string): string[] {
  return splitAddressList(raw)
    .map((p) => p.trim())
    .filter((p) => p !== "" && parseAddress(p) === undefined);
}

/** A comma/semicolon separated list as typed in To/Cc (commas inside quotes are kept). Invalid entries are dropped. */
export function parseAddressList(raw: string): MailAddress[] {
  const seen = new Set<string>();
  const out: MailAddress[] = [];
  for (const p of splitAddressList(raw)) {
    if (p.trim() === "") continue;
    const a = parseAddress(p);
    if (a === undefined || seen.has(a.address)) continue;
    seen.add(a.address);
    out.push(a);
  }
  return out;
}

/** One entry of a To/Cc field: `text` is what the field keeps (a formatted address, or what was typed if it isn't one). */
export type RecipientChip = { text: string; label: string; valid: boolean; address?: string };

function recipientChip(text: string): RecipientChip {
  const t = text.trim();
  const a = parseAddress(t);
  return a === undefined ? { text: t, label: t, valid: false } : { text: formatAddress(a), label: a.name ?? a.address, valid: true, address: a.address };
}

/** A To/Cc value as chips, in order; the same address twice is one chip. */
export function recipientChips(raw: string): RecipientChip[] {
  const seen = new Set<string>();
  const out: RecipientChip[] = [];
  for (const p of splitAddressList(raw)) {
    if (p.trim() === "") continue;
    const c = recipientChip(p);
    if (c.address !== undefined) {
      if (seen.has(c.address)) continue;
      seen.add(c.address);
    }
    out.push(c);
  }
  return out;
}

/** A reply's closed recipient line: everyone it goes to, the first `shown` by name, then "+N". */
export function recipientSummary(lists: readonly string[], shown = Number.MAX_SAFE_INTEGER): string {
  const all = recipientChips(lists.filter((l) => l.trim() !== "").join(", "));
  const names = all.slice(0, shown).map((c) => c.label).join(", ");
  return all.length > shown ? `${names} +${all.length - shown}` : names;
}

/** How many recipients in these fields can't be sent to. */
export function invalidRecipientCount(lists: readonly string[]): number {
  return lists.reduce((n, l) => n + recipientChips(l).filter((c) => !c.valid).length, 0);
}

/** Addresses pasted with only spaces between them are a list; a name and an address aren't. */
function spacedAddresses(part: string): string[] {
  const words = part.trim().split(/\s+/);
  return words.length > 1 && words.every((w) => normalizeAddress(w) !== undefined) ? words : [part.trim()];
}

/** What's typed or pasted into a To/Cc field: the entries it finished (a separator came after them) and the text still being typed. */
export function typedRecipients(typed: string): { done: string[]; rest: string } {
  const parts = splitAddressList(typed);
  const last = parts.pop()!;
  const done = parts.filter((p) => p.trim() !== "").flatMap(spacedAddresses);
  const spaced = spacedAddresses(last);
  return spaced.length > 1 ? { done: [...done, ...spaced], rest: "" } : { done, rest: last.trimStart() };
}

/** The value a To/Cc field keeps: its chips, then the text still being typed. */
export function joinRecipients(chips: readonly string[], rest: string): string {
  return (rest.trim() === "" ? chips : [...chips, rest.trim()]).join(", ");
}

/** The fields of a MailContact ranking needs. */
export type RankableContact = { email: string; name?: string | undefined; messageCount: number; lastAt?: number | undefined };

/** How often and how recently I mail someone: more mail counts less and less, and half as much every 60 days. */
function contactWeight(c: RankableContact, now: number): number {
  const recency = c.lastAt === undefined ? 0.05 : 0.5 ** (Math.max(0, now - c.lastAt) / (60 * 86_400_000));
  return (1 + Math.log2(1 + c.messageCount)) * recency;
}

/**
 * Type-ahead for a To/Cc field: contacts matching `query` (accents and case aside) and not in `exclude`. A name or an
 * address starting with it first, then a word in them, then anywhere; within those, whom I mail most and latest.
 */
export function rankContacts<T extends RankableContact>(contacts: readonly T[], query: string, now: number, exclude: readonly string[], limit = 8): T[] {
  const q = fold(query.trim());
  if (q === "") return [];
  const out = new Set(exclude.map((a) => a.toLowerCase()));
  const tier = (c: T) => {
    const name = fold(c.name ?? "");
    const email = c.email.toLowerCase();
    if (name.startsWith(q) || email.startsWith(q)) return 0;
    if (`${name} ${email}`.split(/[\s._+@-]+/).some((w) => w.startsWith(q))) return 1;
    return name.includes(q) || email.includes(q) ? 2 : 3;
  };
  return contacts
    .filter((c) => !out.has(c.email.toLowerCase()))
    .map((c) => ({ c, tier: tier(c), weight: contactWeight(c, now) }))
    .filter((r) => r.tier < 3)
    .sort((a, b) => a.tier - b.tier || b.weight - a.weight || (a.c.email < b.c.email ? -1 : a.c.email > b.c.email ? 1 : 0))
    .slice(0, limit)
    .map((r) => r.c);
}

/** For headers and the composer: `Ana Souza <ana@x.com>` (quoted when the name has specials), or the bare address. */
export function formatAddress(a: MailAddress): string {
  if (a.name === undefined || a.name === "") return a.address;
  const needsQuotes = /[",;:<>@()[\]\\.]/.test(a.name);
  const name = needsQuotes ? `"${a.name.replace(/(["\\])/g, "\\$1")}"` : a.name;
  return `${name} <${a.address}>`;
}

/** What a row shows for a sender: the name, else the address's local part. */
export function displayName(a: MailAddress): string {
  if (a.name !== undefined && a.name.trim() !== "") return a.name.trim();
  const at = a.address.indexOf("@");
  return at < 0 ? a.address : a.address.slice(0, at);
}

// ---------------------------------------------------------------------------------------------------------------
// Subjects, snippets, replies

/** Reply/forward prefixes in the languages we serve (Re, Fwd, Fw; pt: RES, ENC, Enc; de: AW, WG; es: RV). */
const PREFIX = /^\s*(re|fwd?|res|enc|aw|wg|rv|tr|sv|vs)\s*(\[\d+\])?\s*:\s*/i;

/** The subject without any stack of reply/forward prefixes (threading and display). */
export function normalizeSubject(subject: string): string {
  let s = subject.replace(/\s+/g, " ").trim();
  for (let i = 0; i < 20; i++) {
    const next = s.replace(PREFIX, "");
    if (next === s) break;
    s = next;
  }
  return s;
}

/** "Re: <subject>" (one prefix, never stacked). */
export function replySubject(subject: string): string {
  const base = normalizeSubject(subject);
  return base === "" ? "Re:" : `Re: ${base}`;
}

/** "Fwd: <subject>". */
export function forwardSubject(subject: string): string {
  const base = normalizeSubject(subject);
  return base === "" ? "Fwd:" : `Fwd: ${base}`;
}

/** Invisible padding senders put in a preheader, as characters or as literal entities in a text part. */
const SNIPPET_INVISIBLE = /͏|឴|឵|[­؜ᅟᅠ᠎​-‏‪-‮⁠-⁤ㅤ﻿ﾠ]|&(?:zwnj|zwj|shy|lrm|rlm);/gi;
const SNIPPET_SPACE = /&nbsp;|&#160;|&#xa0;/gi;

const SNIPPET_NUMERIC = /&#(?:(\d{1,7})|x([0-9a-f]{1,6}));/gi;
const SNIPPET_TAG = /<\/?[a-z][a-z0-9]*(?:\s[^<>]*)?\/?>/gi;
const SNIPPET_URL = /<?https?:\/\/\S+>?/gi;
/** An entity a stored snippet was cut through. */
const SNIPPET_CUT_ENTITY = /(?:^|\s)&#?[0-9a-z]{0,8}…$/i;

/** Quoted lines and signatures dropped, whitespace collapsed. */
function previewBody(text: string): string {
  const lines: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (t === "-- " || t === "--") break;
    if (t.startsWith(">")) continue;
    if (/^(on|em) .+(wrote|escreveu):$/i.test(t)) break;
    lines.push(t);
  }
  return lines.join(" ").replace(/\s+/g, " ").trim();
}

/**
 * The thread row's preview, cut at SNIPPET_LENGTH. Marketing text parts are often leftovers of the HTML: invisible
 * padding, tags, numeric entities and tracking links go, a message that is only a link keeps it.
 */
export function snippetOf(text: string, max: number = SNIPPET_LENGTH): string {
  const clean = text
    .replace(SNIPPET_NUMERIC, (_, dec: string | undefined, hex: string | undefined) => {
      const cp = dec !== undefined ? Number(dec) : parseInt(hex ?? "", 16);
      return cp > 0 && cp <= 0x10ffff && (cp < 0xd800 || cp > 0xdfff) ? String.fromCodePoint(cp) : "";
    })
    .replace(SNIPPET_INVISIBLE, "")
    .replace(SNIPPET_SPACE, " ")
    .replace(SNIPPET_TAG, "")
    .replace(SNIPPET_CUT_ENTITY, "");
  const s = previewBody(clean.replace(SNIPPET_URL, "")) || previewBody(clean);
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}

/** The message being replied to, as much of it as reply rules need. */
export interface ReplySource {
  from: MailAddress;
  to: MailAddress[];
  cc: MailAddress[];
  replyTo?: MailAddress | undefined;
  /** MailMessage.listPost: Reply all includes the list. */
  listPost?: string | undefined;
}

/**
 * Who a reply goes to. Reply: the Reply-To (else the sender; a Reply-To of mine doesn't count); when I sent the
 * original, its To, or me when I only wrote to myself. Reply all adds the other To/Cc recipients and the list's
 * posting address in Cc, never me (`myAddress`) and never twice.
 */
export function replyRecipients(source: ReplySource, myAddress: string | string[], replyAll: boolean): { to: MailAddress[]; cc: MailAddress[] } {
  // Every address of mine (aliases, alias domains, groups I send as) counts as me.
  const mine = new Set((Array.isArray(myAddress) ? myAddress : [myAddress]).map((a) => a.toLowerCase()));
  const fromMe = mine.has(source.from.address.toLowerCase());
  const others = source.to.filter((a) => !mine.has(a.address.toLowerCase()));
  const replyTo = source.replyTo !== undefined && !mine.has(source.replyTo.address.toLowerCase()) ? source.replyTo : source.from;
  const to = !fromMe ? [replyTo] : others.length > 0 ? others : [source.from];
  if (!replyAll) return { to, cc: [] };
  const seen = new Set([...mine, ...to.map((a) => a.address.toLowerCase())]);
  const cc: MailAddress[] = [];
  for (const a of [...source.to, ...source.cc, ...(source.listPost === undefined ? [] : [{ address: source.listPost }])]) {
    const k = a.address.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    cc.push(a);
  }
  return { to, cc };
}

const NO_REPLY_WORDS = ["noreply", "donotreply", "dontreply", "donotrespond", "naoresponda", "naoresponder", "nãoresponda"];

/** An address nobody reads (noreply@, do-not-reply@, nao-responda@, mailer-daemon@): worth a word before replying. */
export function isNoReplyAddress(address: string): boolean {
  const at = address.lastIndexOf("@");
  const local = (at < 0 ? address : address.slice(0, at)).toLowerCase().replace(/[-_.]/g, "");
  return local.startsWith("mailerdaemon") || NO_REPLY_WORDS.some((w) => local.includes(w));
}

/** The first no-reply address in a typed To list, for the reply box's notice. */
export function noReplyRecipient(raw: string): string | undefined {
  return parseAddressList(raw).find((a) => isNoReplyAddress(a.address))?.address;
}

export type ReplyKind = "reply" | "replyAll" | "forward";

/** Who Reply and Reply all go to, and both subjects, so the box can switch between them and Forward in place. */
export interface ReplyTargets {
  reply: { to: MailAddress[]; cc: MailAddress[] };
  replyAll: { to: MailAddress[]; cc: MailAddress[] };
  subject: string;
  forwardSubject: string;
}

/**
 * Switching Reply ⇄ Reply all: `into`'s people, plus whoever I added myself. Only those who came with the kind I'm
 * leaving go; someone I typed stays, in the field I typed them in.
 */
export function switchRecipients(
  current: { to: MailAddress[]; cc: MailAddress[] },
  from: { to: MailAddress[]; cc: MailAddress[] },
  into: { to: MailAddress[]; cc: MailAddress[] },
): { to: MailAddress[]; cc: MailAddress[] } {
  const key = (a: MailAddress) => a.address.toLowerCase();
  const came = new Set([...from.to, ...from.cc].map(key));
  const seen = new Set([...into.to, ...into.cc].map(key));
  const mine = (list: MailAddress[]) =>
    list.filter((a) => {
      const k = key(a);
      if (came.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  return { to: [...into.to, ...mine(current.to)], cc: [...into.cc, ...mine(current.cc)] };
}

/** The kind a saved draft is: Forward by its mode; Reply all once it has someone only Reply all brings. */
export function draftReplyKind(mode: "new" | "reply" | "forward", to: MailAddress[], cc: MailAddress[], targets: ReplyTargets): ReplyKind {
  if (mode === "forward") return "forward";
  const key = (a: MailAddress) => a.address.toLowerCase();
  const reply = new Set([...targets.reply.to, ...targets.reply.cc].map(key));
  const have = new Set([...to, ...cc].map(key));
  return [...targets.replyAll.to, ...targets.replyAll.cc].some((a) => !reply.has(key(a)) && have.has(key(a))) ? "replyAll" : "reply";
}

/**
 * A reloaded thread's messages: a message that came `textPartial` keeps the full text I already have, so a reload
 * never loses it or reads it again.
 */
export function mergeThreadMessages(fresh: MailMessage[], known: MailMessage[]): MailMessage[] {
  const full = new Map(known.filter((m) => m.textPartial !== true).map((m) => [m.id, m.text]));
  return fresh.map((m) => {
    const text = full.get(m.id);
    if (m.textPartial !== true || text === undefined) return m;
    const { textPartial: _, ...whole } = m;
    void _;
    return { ...whole, text };
  });
}

/** One thread the reader keeps on the device: when it was last opened, and about how big it is. */
export type MailThreadCacheEntry = { id: string; at: number; bytes: number };

/** The reader's device cache: threads kept, and their total size. */
export const MAIL_THREAD_CACHE_MAX = 50;
export const MAIL_THREAD_CACHE_BYTES = 10_000_000;
/** One thread's share of it: past this its oldest bodies aren't kept (they load when opened). */
export const MAIL_THREAD_ENTRY_BYTES = 1_000_000;

/** Keeps the threads opened most recently, up to `maxCount` and `maxBytes`, newest first; `drop` is what goes. */
export function trimMailThreadCache(entries: MailThreadCacheEntry[], maxCount: number, maxBytes: number): { keep: MailThreadCacheEntry[]; drop: string[] } {
  const sorted = [...entries].sort((a, b) => b.at - a.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const keep: MailThreadCacheEntry[] = [];
  const drop: string[] = [];
  let bytes = 0;
  for (const e of sorted) {
    if (keep.length < maxCount && bytes + e.bytes <= maxBytes) {
      keep.push(e);
      bytes += e.bytes;
    } else drop.push(e.id);
  }
  return { keep, drop };
}

/** The original quoted under a reply: the attribution line, then every line prefixed with "> ". */
export function quoteText(text: string, attribution: string): string {
  const body = text.replace(/\r\n/g, "\n").replace(/\n+$/, "");
  return `${attribution}\n${body.split("\n").map((l) => (l === "" ? ">" : `> ${l}`)).join("\n")}`;
}

/** The longest message the server takes (contracts SendMailRequest.text), and the most people one can go to. */
export const MAIL_TEXT_MAX = 500_000;
export const MAIL_RECIPIENTS_MAX = 500;
/** How much of the original a reply or a forward starts with: the room left is for what I write. */
export const REPLY_QUOTE_MAX = 100_000;
export const FORWARD_QUOTE_MAX = 400_000;

/** A quote no longer than `max` (UTF-16 units): whole lines from the top, the newest text, then a `[…]` line. */
export function fitQuote(quote: string, max: number): string {
  if (quote.length <= max) return quote;
  const cut = "\n>\n> […]";
  return `${quote.slice(0, Math.max(quote.lastIndexOf("\n", max - cut.length), 0))}${cut}`;
}

/**
 * A reply's text as the box shows it: what I write (my signature with it) and the quote beside it, behind "•••". The
 * draft and the send keep them as one text (`joinReplyText`); `main` comes back as typed, trailing lines included.
 */
export function splitReplyText(text: string): { main: string; quote: string } {
  const t = text.replace(/\r\n/g, "\n").replace(/\n+$/, "");
  // A placeholder line first: splitQuote keeps an all-quote text as the body (nothing written yet is still a quote here).
  const quote = splitQuote(`.\n${t}`).quote;
  if (quote === "") return { main: text, quote: "" };
  const main = t.slice(0, t.length - quote.length);
  return { main: main.endsWith("\n\n") ? main.slice(0, -2) : main.replace(/\n+$/, ""), quote };
}

export function joinReplyText(p: { main: string; quote: string }): string {
  return p.quote === "" ? p.main : `${p.main}\n\n${p.quote}`;
}

/**
 * Gmail's "trimmed content": the quoted reply at the end of a plain-text message — its attribution line (the short
 * line ending in ":" just before it: "On … wrote:", "Em … escreveu:", our "2026-10-20 14:03, Bob <bob@y.com>:") and the `>` lines after it — split from what the sender wrote, so the reader can hide it behind
 * "•••". `quote` is "" when the message ends with no quote; quotes in the middle (inline replies) stay in `body`.
 */
export function splitQuote(text: string): { body: string; quote: string } {
  const lines = text.replace(/\r\n/g, "\n").replace(/\n+$/, "").split("\n");
  let start = lines.length;
  while (start > 0 && (lines[start - 1]!.trim() === "" || lines[start - 1]!.trimStart().startsWith(">"))) start--;
  if (!lines.slice(start).some((l) => l.trimStart().startsWith(">"))) return { body: text, quote: "" };
  let head = start;
  while (head > 0 && lines[head - 1]!.trim() === "") head--;
  if (head > 0) {
    const line = lines[head - 1]!.trim();
    if (line.endsWith(":") && line.length <= 200) start = head - 1;
  }
  const body = lines.slice(0, start).join("\n").replace(/\s+$/, "");
  if (body === "") return { body: text, quote: "" };
  return { body, quote: lines.slice(start).join("\n").replace(/^\n+/, "") };
}

/** Message-IDs a message points at (In-Reply-To first, then References newest first), deduplicated, without <>. */
export function referencedIds(inReplyTo: string | undefined, references: string | undefined): string[] {
  const ids: string[] = [];
  const add = (raw: string | undefined) => {
    for (const m of (raw ?? "").matchAll(/<([^<>\s]+)>/g)) if (!ids.includes(m[1]!)) ids.push(m[1]!);
  };
  add(inReplyTo);
  const refs: string[] = [];
  for (const m of (references ?? "").matchAll(/<([^<>\s]+)>/g)) refs.push(m[1]!);
  for (const r of refs.reverse()) if (!ids.includes(r)) ids.push(r);
  return ids;
}

// ---------------------------------------------------------------------------------------------------------------
// Tabs and contacts

/** What the rule-based fallback sees about an incoming message. */
export interface TabSignals {
  fromAddress: string;
  /** The workspace's own mail domains (verified). */
  workspaceDomains: string[];
  /** The sender is a workspace member (by address). */
  fromMember: boolean;
  /** The sender's contact kind, when the contact exists. */
  contactKind?: ContactKind | undefined;
  /** A person moved this sender's mail to a tab before ("Move to Clients"). */
  override?: MailTabId | undefined;
  /** Bulk-mail headers. */
  listUnsubscribe: boolean;
  precedence?: string | undefined;
  /** A no-reply style sender. */
  autoSubmitted?: string | undefined;
  /** Looks like a service's notice, not marketing (the API's mail/transactional.ts). Only ever outweighs bulk headers. */
  transactional?: boolean | undefined;
}

/**
 * The tab rules alone decide (a person's override, a team sender, a known client), or undefined when it's the
 * classifier's call. Used before the classifier and as its fallback (`fallbackTab`).
 */
export function ruleTab(s: TabSignals): MailTabId | undefined {
  if (s.override !== undefined) return s.override;
  const domain = addressDomain(s.fromAddress);
  if (s.fromMember || s.workspaceDomains.includes(domain) || s.contactKind === "team") return "team";
  if (s.contactKind === "client") return "clients";
  return undefined;
}

const NO_REPLY = /^(no-?reply|do-?not-?reply|notifications?|alerts?|mailer-daemon|bounces?|news(letter)?|info|marketing|updates?)([+.\-_].*)?@/i;

/**
 * The tab when the classifier isn't available or unsure: the rules, then automated mail (Updates, even with bulk
 * headers, which services put on receipts too), then bulk headers (Promotions), then no-reply senders, then Email.
 */
export function fallbackTab(s: TabSignals): MailTabId {
  const rule = ruleTab(s);
  if (rule !== undefined) return rule;
  const prec = (s.precedence ?? "").toLowerCase();
  const bulk = s.listUnsubscribe || prec === "bulk" || prec === "list";
  if ((s.autoSubmitted !== undefined && s.autoSubmitted.toLowerCase() !== "no") || (bulk && s.transactional === true)) return "updates";
  if (bulk) return "promotions";
  return NO_REPLY.test(s.fromAddress) ? "updates" : "email";
}

/** The classifier's pick when it's confident enough, else the fallback. A rule always wins. */
export function chooseTab(s: TabSignals, ai: { tab: MailTabId; confidence: number } | undefined): MailTabId {
  const rule = ruleTab(s);
  if (rule !== undefined) return rule;
  if (ai !== undefined && ai.confidence >= MIN_TAB_CONFIDENCE) return ai.tab;
  return fallbackTab(s);
}

/** The contact kind a tab implies (Clients → client, Team → team, anything else → email). */
export function contactKindForTab(tab: MailTabId): ContactKind {
  return tab === "clients" ? "client" : tab === "team" ? "team" : "email";
}

/** The tab a contact kind sends mail to (`email` leaves it to the classifier: undefined). */
export function tabForContactKind(kind: ContactKind): MailTab | undefined {
  return kind === "client" ? "clients" : kind === "team" ? "team" : undefined;
}

/** New-mail notifications by default: Priority mail only, whatever its tab (Gmail's "High priority only"). */
export const DEFAULT_MAIL_NOTIFY_PREFS: MailNotifyPrefs = { enabled: true, priority: true, tabs: [], view: "original" };

/**
 * Whether new mail in `tab`/`folder` notifies someone with these preferences (none saved = the defaults): it's in
 * Priority (`priority`) and Priority notifies (absent = it does), or its tab is one they picked. Spam never does.
 */
export function mailNotifies(prefs: MailNotifyPrefs | undefined, tab: MailTabId, folder: string, priority = false): boolean {
  if (folder !== "inbox") return false;
  const p = prefs ?? DEFAULT_MAIL_NOTIFY_PREFS;
  return p.enabled && ((priority && p.priority !== false) || p.tabs.includes(tab));
}

// ---------------------------------------------------------------------------------------------------------------
// Priority

/** What the Priority rule sees about a conversation: the thread's own fields plus the marks on it and its sender. */
export interface PrioritySignals {
  /** Its tab is one people write to (MailTabDef.waitsOnMe): mail waiting on me there counts; elsewhere only a mark does. */
  waitsOnMe: boolean;
  folder: string;
  starred: boolean;
  /** Waiting on my reply (the classifier's answer; cleared when I reply, set when a "remind me" comes back). */
  needsReply?: boolean | undefined;
  phishing?: boolean | undefined;
  snoozedUntil?: number | undefined;
  /** When a "Remind me if no reply" came back unanswered. */
  remindedAt?: number | undefined;
  /** "Mark as priority" (true) or "Not priority" (false) on this conversation. */
  mark?: boolean | undefined;
  /** The same mark on its sender, taught by an earlier conversation. */
  senderMark?: boolean | undefined;
}

/** Why a conversation is in Priority (the marker's tooltip). */
export type PriorityReason = "marked" | "starred" | "sender" | "reminder" | "waiting";

/**
 * Why a conversation is in Priority, or undefined when it isn't (Gmail's Priority Inbox, Superhuman's Important). Only
 * inbox mail that isn't phishing or snoozed; then the first that applies decides: my mark on this conversation, a star,
 * my mark on the sender, and last whether it's waiting on me in a tab people write to (a returned reminder counts).
 */
export function priorityReason(s: PrioritySignals, now: number): PriorityReason | undefined {
  if (s.folder !== "inbox" || s.phishing === true) return undefined;
  if (s.snoozedUntil !== undefined && s.snoozedUntil > now) return undefined;
  if (s.mark !== undefined) return s.mark ? "marked" : undefined;
  if (s.starred) return "starred";
  if (s.senderMark !== undefined) return s.senderMark ? "sender" : undefined;
  if (s.needsReply === true && s.waitsOnMe) return s.remindedAt !== undefined ? "reminder" : "waiting";
  return undefined;
}

/** The tabs whose waiting mail counts for Priority (MailTabDef.waitsOnMe). */
export function mailWaitTabs(defs: readonly MailTabDef[]): string[] {
  return defs.filter((d) => d.kind === "category" && d.waitsOnMe === true).map((d) => d.id);
}

/** `priorityReason` for a thread as the API sends it (its marks are `priorityMark` / `senderPriority`); `waitTabs`: mailWaitTabs. */
export function threadPriority(t: MailThread, now: number, waitTabs: readonly string[]): PriorityReason | undefined {
  return priorityReason(
    {
      waitsOnMe: waitTabs.includes(t.tab), folder: t.folder, starred: t.starred, needsReply: t.needsReply, phishing: t.phishing, snoozedUntil: t.snoozedUntil,
      remindedAt: t.noReplySince, mark: t.priorityMark, senderMark: t.senderPriority,
    },
    now,
  );
}

// ---------------------------------------------------------------------------------------------------------------
// The tab strip: the server defines it (MailTabDef, apps/api/src/mail/tabs.json) and clients cache what it sent.

/** The kinds of tab this version can draw; the strip leaves out others (a newer server's). */
export const MAIL_TAB_KINDS: readonly string[] = ["priority", "category"];

/** The strip as this client draws it: the server's tabs in its order, without kinds it doesn't know. */
export function stripTabs(defs: readonly MailTabDef[]): MailTabDef[] {
  return defs.filter((d) => MAIL_TAB_KINDS.includes(d.kind));
}

/** The tabs conversations are sorted into ("Move to"), in order. */
export function categoryTabs(defs: readonly MailTabDef[]): MailTabDef[] {
  return defs.filter((d) => d.kind === "category");
}

/** Whether a tab's list runs across tabs, so its rows say which they're in: Priority and All (MailTabDef.listsAll). */
export function tabGathers(def: Pick<MailTabDef, "kind" | "listsAll"> | undefined): boolean {
  return def?.kind === "priority" || def?.listsAll === true;
}

/** A tab's name in `locale` ("pt-BR"): that, then another of its language ("pt"), then English, then its id. */
export function mailTabLabel(def: Pick<MailTabDef, "id" | "labels">, locale: string): string {
  const exact = def.labels[locale];
  if (exact !== undefined) return exact;
  const lang = locale.split("-")[0]!.toLowerCase();
  const same = Object.keys(def.labels).find((k) => k.split("-")[0]!.toLowerCase() === lang);
  return (same === undefined ? undefined : def.labels[same]) ?? def.labels.en ?? def.id;
}

/** What a tab shows when it has nothing to preview: a dash, so every tab keeps its second line. */
export const MAIL_TAB_NO_PREVIEW = "–";

/**
 * A tab's second line (Gmail): its newest thread as "sender — subject", or a dash when it has none, so every tab keeps
 * the same two lines whether a preview is there or not.
 */
export function mailTabPreview(preview: { sender: string; subject: string } | undefined): string {
  if (preview === undefined) return MAIL_TAB_NO_PREVIEW;
  const sender = preview.sender.trim();
  const subject = preview.subject.trim();
  if (sender === "" && subject === "") return MAIL_TAB_NO_PREVIEW;
  if (sender === "") return subject;
  if (subject === "") return sender;
  return `${sender} — ${subject}`;
}

/**
 * The tab Mail opens on: the strip's first, unless that's Priority and it's known to be empty (`total` 0), then the
 * first category. Undefined before the strip is known.
 */
export function mailLandingTab(defs: readonly MailTabDef[], priority: { total: number } | undefined): MailTabId | undefined {
  const tabs = stripTabs(defs);
  const first = tabs[0];
  if (first?.kind === "priority" && priority !== undefined && priority.total === 0) return tabs.find((d) => d.kind === "category")?.id ?? first.id;
  return first?.id;
}

// ---------------------------------------------------------------------------------------------------------------
// People in mail (the contact card: docs/features/mail.md, "People cards")

/** The fields of a MailContact the card needs. */
export type PersonContact = { id: string; email: string; name?: string | undefined; kind: ContactKind; userId?: string | undefined };
/** The fields of a workspace Member the card needs (`email` only when I may see it). */
export type PersonMember = { userId: string; fullName: string; displayName: string; email?: string | undefined; deactivated: boolean };

/** Who an address in mail is: a workspace member, a contact, or someone not in the contacts yet. */
export type MailPerson = {
  kind: "member" | "contact" | "unknown";
  /** Lower case address (as written when it isn't a valid one). */
  address: string;
  /** The contact's name, else the member's full name, else the name in the header, else the address's local part. */
  name: string;
  contactId?: string;
  /** The contact's kind (members are always Team). */
  contactKind?: ContactKind;
  userId?: string;
};

/**
 * Resolves an address from a message header against the contacts and members: an active member (by the contact's
 * `userId`, or the member's visible email) wins, then a contact, else unknown.
 */
export function resolvePerson(a: MailAddress, contacts: readonly PersonContact[], members: readonly PersonMember[]): MailPerson {
  const address = normalizeAddress(a.address) ?? a.address.trim().toLowerCase();
  const contact = contacts.find((c) => c.email.toLowerCase() === address);
  const active = members.filter((m) => !m.deactivated);
  const member = (contact?.userId === undefined ? undefined : active.find((m) => m.userId === contact.userId)) ?? active.find((m) => m.email !== undefined && m.email.toLowerCase() === address);
  const headerName = a.name !== undefined && a.name.trim() !== "" ? a.name.trim() : undefined;
  const name = contact?.name ?? (member === undefined ? undefined : member.fullName || member.displayName) ?? headerName ?? displayName({ address });
  const p: MailPerson = { kind: member !== undefined ? "member" : contact !== undefined ? "contact" : "unknown", address, name };
  if (contact !== undefined) {
    p.contactId = contact.id;
    p.contactKind = member !== undefined ? "team" : contact.kind;
  } else if (member !== undefined) p.contactKind = "team";
  if (member !== undefined) p.userId = member.userId;
  return p;
}

/** What the card offers, in order (the first two are its buttons). */
export type PersonAction = "message" | "viewProfile" | "newEmail" | "openContact" | "addContact" | "addAsClient" | "copyEmail" | "setKind" | "addToProject" | "recentEmails";

/**
 * The card's actions for a person: members get Message (not for me) and View profile; contacts New email and Open
 * contact, then the kind switch and Add to project; unknown senders Add to contacts and Add as client. Changing
 * anything needs Email on (`canChange`); New email needs a mailbox (`canSend`).
 */
export function personActions(p: MailPerson, opts: { canChange: boolean; canSend: boolean; isMe: boolean }): PersonAction[] {
  const out: PersonAction[] = [];
  if (p.kind === "member") {
    if (!opts.isMe) out.push("message");
    out.push("viewProfile");
    if (opts.canSend && !opts.isMe) out.push("newEmail");
    if (p.contactId !== undefined) out.push("openContact");
  } else if (p.kind === "contact") {
    if (opts.canSend) out.push("newEmail");
    out.push("openContact");
    if (opts.canChange) out.push("setKind", "addToProject");
    out.push("recentEmails");
  } else {
    if (opts.canChange) out.push("addContact", "addAsClient");
    if (opts.canSend) out.push("newEmail");
  }
  out.push("copyEmail");
  return out;
}

/** The fields of a MailProject the mail screens need. */
export type PersonProject = { rowId: string; title: string; contactIds: readonly string[]; status?: { group: "todo" | "in_progress" | "complete" } | undefined };

/** A contact's projects: open ones first (in the order given), done ones last. */
export function contactProjects<P extends PersonProject>(contactId: string, projects: readonly P[]): P[] {
  const mine = projects.filter((p) => p.contactIds.includes(contactId));
  return [...mine.filter((p) => p.status?.group !== "complete"), ...mine.filter((p) => p.status?.group === "complete")];
}

/**
 * The project chip on a conversation: when its sender is a client contact in exactly one project that isn't done,
 * that project; otherwise none (Gmail-style chips stay quiet when it's ambiguous).
 */
export function threadProject<P extends PersonProject>(sender: MailAddress, contacts: readonly PersonContact[], projects: readonly P[]): P | undefined {
  const address = normalizeAddress(sender.address);
  const c = contacts.find((x) => x.email.toLowerCase() === address);
  if (c === undefined || c.kind !== "client" || c.userId !== undefined) return undefined;
  const open = contactProjects(c.id, projects).filter((p) => p.status?.group !== "complete");
  return open.length === 1 ? open[0] : undefined;
}

/** The mail search for "Recent emails" with these people: `with:` each address (any of them, from or to). */
export function mailWithSearch(addresses: readonly string[]): string {
  const seen: string[] = [];
  for (const a of addresses) {
    const n = normalizeAddress(a);
    if (n !== undefined && !seen.includes(n)) seen.push(n);
  }
  return seen.map((a) => `with:${a}`).join(" ");
}

/** Contacts list filter: everyone (`undefined`) or one kind; search matches name, address and organization. */
export function matchesContact(c: { email: string; name?: string | undefined; org?: string | undefined; kind: ContactKind }, kind: ContactKind | undefined, q: string): boolean {
  if (kind !== undefined && c.kind !== kind) return false;
  const needle = q.trim().toLowerCase();
  if (needle === "") return true;
  return [c.email, c.name ?? "", c.org ?? ""].some((v) => v.toLowerCase().includes(needle));
}

// ---------------------------------------------------------------------------------------------------------------
// Domain onboarding

/** The SPF record to publish at the root: the existing one with our include added, or a new one. */
export function mergeSpf(existing: string | undefined): string {
  const include = `include:${MAIL_HOST.spf}`;
  const cur = (existing ?? "").trim();
  if (!/^v=spf1(\s|$)/i.test(cur)) return `v=spf1 ${include} ~all`;
  if (cur.split(/\s+/).some((t) => t.toLowerCase() === include)) return cur;
  const parts = cur.split(/\s+/);
  const allAt = parts.findIndex((t) => /^[~?+-]?all$/i.test(t) || /^redirect=/i.test(t));
  if (allAt < 0) return `${cur} ${include} ~all`;
  parts.splice(allAt, 0, include);
  return parts.join(" ");
}

/**
 * The records a workspace adds at its DNS provider to host `domain` on Bundle, in the order the settings table shows
 * them. `domainId` names our side (`<id>.dkim.bundle.direct`, `_dmarc-<id>.bundle.direct`); `verifyToken` proves
 * ownership. Statuses start `unchecked`.
 */
export function mailDnsRecords(domainId: string, verifyToken: string, existingSpf?: string): DnsRecord[] {
  const id = domainId.toLowerCase();
  const r = (rec: Omit<DnsRecord, "status">): DnsRecord => ({ ...rec, status: "unchecked" });
  return [
    r({ type: "TXT", name: "_bundle-verify", value: `bundle-verify=${verifyToken}`, purpose: "verify", required: true }),
    r({ type: "MX", name: "@", value: MAIL_HOST.mx[0], priority: 10, purpose: "mx", required: true }),
    r({ type: "MX", name: "@", value: MAIL_HOST.mx[1], priority: 20, purpose: "mx", required: true }),
    r({ type: "TXT", name: "@", value: mergeSpf(existingSpf), purpose: "spf", required: true }),
    r({ type: "CNAME", name: `${DKIM_SELECTOR}._domainkey`, value: `${id}.${MAIL_HOST.dkim}`, purpose: "dkim", required: true }),
    r({ type: "CNAME", name: "bounce", value: MAIL_HOST.returnPath, purpose: "returnPath", required: true }),
    r({ type: "CNAME", name: "_dmarc", value: `_dmarc-${id}.${MAIL_HOST.dmarc}`, purpose: "dmarc", required: true }),
    r({ type: "CNAME", name: "mta-sts", value: MAIL_HOST.mtaSts, purpose: "mtaSts", required: false }),
  ];
}

/** The custom MAIL FROM subdomain SES bounces through (`send.<domain>`); `bounce` is our own edge's return path. */
export const SES_MAIL_FROM_LABEL = "send";

/**
 * The custom MAIL FROM records for Amazon SES in `region` (what Resend and SES's own console list): an MX to SES's
 * feedback host and an SPF record, at `send.<domain>`. With them the Return-Path is on the customer's domain, so SPF
 * aligns for DMARC as well as DKIM. Optional: without them SES uses its own amazonses.com bounce address.
 */
export function sesMailFromRecords(region: string): DnsRecord[] {
  const r = (rec: Omit<DnsRecord, "status">): DnsRecord => ({ ...rec, status: "unchecked" });
  return [
    r({ type: "MX", name: SES_MAIL_FROM_LABEL, value: `feedback-smtp.${region}.amazonses.com`, priority: 10, purpose: "mailFrom", required: false }),
    r({ type: "TXT", name: SES_MAIL_FROM_LABEL, value: "v=spf1 include:amazonses.com ~all", purpose: "mailFrom", required: false }),
  ];
}

/**
 * Optional records that let mail apps set themselves up from just the address and an app password (Fastmail publishes
 * the same SRV set): `autoconfig` / `autodiscover` CNAMEs to our hosts (Thunderbird and its Android app, Outlook's
 * autodiscover; Bundle gets each name a certificate through Cloudflare for SaaS once the CNAME is in place) and the
 * RFC 6186 SRV records for IMAP and submission, `_autodiscover._tcp` (Outlook's last autodiscover step) and RFC 6764's
 * `_caldavs._tcp` / `_carddavs._tcp` for calendar and contacts apps. The SRV
 * rows carry the target in `value`, with `priority`, `weight` and `port`.
 */
export function mailClientSetupRecords(): DnsRecord[] {
  const r = (rec: Omit<DnsRecord, "status" | "purpose" | "required">): DnsRecord => ({ ...rec, purpose: "clientSetup", required: false, status: "unchecked" });
  return [
    r({ type: "CNAME", name: "autoconfig", value: MAIL_HOST.autoconfig }),
    r({ type: "CNAME", name: "autodiscover", value: MAIL_HOST.autodiscover }),
    r({ type: "SRV", name: "_imaps._tcp", value: MAIL_CLIENT_HOSTS.imap, priority: 0, weight: 1, port: 993 }),
    r({ type: "SRV", name: "_submissions._tcp", value: MAIL_CLIENT_HOSTS.smtp, priority: 0, weight: 1, port: 465 }),
    r({ type: "SRV", name: "_autodiscover._tcp", value: MAIL_HOST.autodiscover, priority: 0, weight: 1, port: 443 }),
    // RFC 6764: calendar and contacts apps given just the address find CalDAV / CardDAV (/.well-known/ on that host).
    r({ type: "SRV", name: "_caldavs._tcp", value: MAIL_CLIENT_HOSTS.caldav, priority: 0, weight: 1, port: 443 }),
    r({ type: "SRV", name: "_carddavs._tcp", value: MAIL_CLIENT_HOSTS.carddav, priority: 0, weight: 1, port: 443 }),
  ];
}

/** The records plus any client-setup rows they lack (domains added before them get them on their next check). */
export function withClientSetup(records: DnsRecord[]): DnsRecord[] {
  const key = (x: DnsRecord) => `${x.type} ${x.name}`;
  const have = new Set(records.filter((x) => x.purpose === "clientSetup").map(key));
  return [...records, ...mailClientSetupRecords().filter((x) => !have.has(key(x)))];
}

/**
 * The records as a BIND zone file, for providers that import one (Cloudflare: DNS → Records → Import and Export;
 * GoDaddy, Route 53…): absolute names, TTL 3600, TXT values in quoted 255-byte chunks. Only what Bundle needs.
 */
export function mailZoneFile(domain: string, records: DnsRecord[]): string {
  const fqdn = (n: string) => `${recordFqdn(n, domain)}.`;
  const txt = (v: string) => {
    const chunks: string[] = [];
    for (let i = 0; i < v.length; i += 255) chunks.push(`"${v.slice(i, i + 255).replace(/(["\\])/g, "\\$1")}"`);
    return chunks.join(" ");
  };
  const lines = [`; Bundle Mail records for ${domain}`, `; Import at your DNS provider, then remove any other MX records for ${domain}.`, `$ORIGIN ${domain}.`, "$TTL 3600"];
  for (const r of records) {
    if (r.type === "MX") lines.push(`${fqdn(r.name)} 3600 IN MX ${r.priority ?? 10} ${r.value}.`);
    else if (r.type === "CNAME") lines.push(`${fqdn(r.name)} 3600 IN CNAME ${r.value}.`);
    else if (r.type === "SRV") lines.push(`${fqdn(r.name)} 3600 IN SRV ${r.priority ?? 0} ${r.weight ?? 1} ${r.port ?? 0} ${r.value}.`);
    else lines.push(`${fqdn(r.name)} 3600 IN TXT ${txt(r.value)}`);
  }
  return `${lines.join("\n")}\n`;
}

/** The fully qualified name of a record (`@` → the domain itself). */
export function recordFqdn(name: string, domain: string): string {
  return name === "@" ? domain : `${name}.${domain}`;
}

/** Receiving needs ownership + both MX; sending needs ownership + DKIM + return path. SPF/DMARC are advised. */
export function domainCapabilities(records: DnsRecord[]): { canReceive: boolean; canSend: boolean } {
  const ok = (p: DnsRecord["purpose"]) => records.filter((x) => x.purpose === p).every((x) => x.status === "ok") && records.some((x) => x.purpose === p);
  const owned = ok("verify");
  return { canReceive: owned && ok("mx"), canSend: owned && ok("dkim") && ok("returnPath") };
}

/** The ownership record (`_bundle-verify` TXT) was found: the domain is the workspace's, wherever its mail goes. */
export function ownershipVerified(records: DnsRecord[]): boolean {
  const verify = records.filter((x) => x.purpose === "verify");
  return verify.length > 0 && verify.every((x) => x.status === "ok");
}

/**
 * Where a domain's mail stands (Settings → Email, the mailbox forms): mailboxes can live on it once its ownership is
 * verified, even while its MX stays at the current provider ("No, keep it where it is": mail then comes in through
 * the Google import or IMAP sync). `receive`: "mx" = its MX points at Bundle; "google" / "elsewhere" = still at
 * Google Workspace (seen in its MX hosts) or another provider; "none" = ownership not verified yet. `send`: its
 * DKIM and return-path records are done (`canSend`).
 */
export function domainMailState(d: { canReceive: boolean; canSend: boolean; records: DnsRecord[] }): { hostsMailboxes: boolean; receive: "mx" | "google" | "elsewhere" | "none"; send: boolean } {
  const owned = d.canReceive || ownershipVerified(d.records);
  if (!owned) return { hostsMailboxes: false, receive: "none", send: d.canSend };
  if (d.canReceive) return { hostsMailboxes: true, receive: "mx", send: d.canSend };
  const hosts = d.records
    .filter((x) => x.purpose === "mx" && x.found !== undefined)
    .flatMap((x) => x.found!.split(/,\s*/).map((a) => (a.trim().split(/\s+/).pop() ?? "").toLowerCase().replace(/\.$/, "")));
  const google = hosts.some((h) => h === "google.com" || h.endsWith(".google.com") || h.endsWith(".googlemail.com"));
  return { hostsMailboxes: true, receive: google ? "google" : "elsewhere", send: d.canSend };
}

/** A quick choice in Gmail's Snooze / Schedule send menus: what it's called and when it fires. */
export type MailTimePreset = { id: "laterToday" | "tomorrowMorning" | "tomorrowAfternoon" | "thisWeekend" | "nextWeek" | "mondayMorning"; at: number };

function atLocal(dayTs: number, hour: number, tz: string): number {
  const p = localParts(dayTs, tz);
  return fromLocal(p.year, p.month, p.day, hour, 0, tz);
}

/** The next `weekday` (0 Sunday … 6 Saturday) strictly after today, at `hour`. */
function nextWeekday(now: number, weekday: number, hour: number, tz: string): number {
  const today = localParts(now, tz).weekday;
  const ahead = ((weekday - today + 7) % 7) || 7;
  return atLocal(addDays(now, ahead, tz), hour, tz);
}

/**
 * Gmail's Snooze menu: later today (18:00, only before 17:00), tomorrow (8:00), this weekend (Saturday 8:00, not on a
 * weekend) and next week (Monday 8:00), in the person's time zone.
 */
export function snoozePresets(now: number, timeZone: string): MailTimePreset[] {
  const p = localParts(now, timeZone);
  const out: MailTimePreset[] = [];
  if (p.hour < 17) out.push({ id: "laterToday", at: atLocal(now, 18, timeZone) });
  out.push({ id: "tomorrowMorning", at: atLocal(addDays(now, 1, timeZone), 8, timeZone) });
  if (p.weekday >= 1 && p.weekday <= 5) out.push({ id: "thisWeekend", at: nextWeekday(now, 6, 8, timeZone) });
  out.push({ id: "nextWeek", at: nextWeekday(now, 1, 8, timeZone) });
  return out;
}

/** Gmail's Schedule send menu: tomorrow morning (8:00), tomorrow afternoon (13:00), Monday morning (8:00). */
export function schedulePresets(now: number, timeZone: string): MailTimePreset[] {
  const tomorrow = addDays(now, 1, timeZone);
  const out: MailTimePreset[] = [
    { id: "tomorrowMorning", at: atLocal(tomorrow, 8, timeZone) },
    { id: "tomorrowAfternoon", at: atLocal(tomorrow, 13, timeZone) },
  ];
  const monday = nextWeekday(now, 1, 8, timeZone);
  if (monday !== out[0]!.at) out.push({ id: "mondayMorning", at: monday });
  return out;
}

/** What follows the part I write: my signature ("-- ") and the quoted original, kept by "Help me write". */
export function composerTail(text: string): string {
  // A placeholder line first: splitQuote keeps an all-quote text as the body (nothing written yet is still a quote here).
  const quote = splitQuote(`.\n${text}`).quote;
  const quoteAt = quote === "" ? text.length : text.length - quote.length;
  const sig = text.indexOf("\n-- \n");
  const at = Math.min(quoteAt, sig < 0 ? text.length : sig);
  return at >= text.length ? "" : `\n\n${text.slice(at).replace(/^\n+/, "")}`;
}

/**
 * The composer's text in its three parts (Spark's layout): what I write, my signature ("-- " block, without the
 * marker) and the quoted original below them. `joinComposer` puts them back the way they're sent.
 */
export function splitComposer(text: string): { body: string; signature: string | undefined; quote: string } {
  const tail = composerTail(text);
  const body = text.slice(0, text.length - tail.length).replace(/\s+$/, "");
  const rest = tail.replace(/^\n+/, "");
  if (!rest.startsWith("-- \n")) return { body, signature: undefined, quote: rest };
  const quote = splitQuote(`.\n${rest}`).quote;
  const sig = rest.slice(4, rest.length - quote.length).replace(/\s+$/, "");
  return { body, signature: sig, quote: quote.replace(/^\n+/, "") };
}

export function joinComposer(p: { body: string; signature: string | undefined; quote: string }): string {
  const parts = [p.body.replace(/\s+$/, "")];
  if (p.signature !== undefined) parts.push(`-- \n${p.signature}`);
  if (p.quote !== "") parts.push(p.quote);
  return parts.join("\n\n");
}

/**
 * My signature as Settings → Email saves it (the composer's markdown: `*bold*`, `_italic_`, `[text](url)`, line
 * breaks): leading blank lines and a typed "-- " line dropped (the composer adds that marker), trailing space
 * trimmed. undefined: no signature.
 */
export function cleanSignature(text: string): string | undefined {
  const t = text.replace(/\r\n/g, "\n").replace(/^\s*\n/, "").replace(/^-- ?\n/, "").replace(/\s+$/, "");
  return t.trim() === "" ? undefined : t;
}

/**
 * The signature a message from this mailbox starts with (Gmail's "For new emails use" / "On reply/forward use"):
 * mine, if I have one and it's on for this kind of message (absent options mean on). undefined: none.
 */
export function signatureFor(
  mailbox: { signature?: string | undefined; signatureOnNew?: boolean | undefined; signatureOnReply?: boolean | undefined } | undefined,
  mode: "new" | "reply" | "forward",
): string | undefined {
  const sig = mailbox?.signature;
  if (sig === undefined || sig.trim() === "") return undefined;
  return (mode === "new" ? mailbox?.signatureOnNew : mailbox?.signatureOnReply) === false ? undefined : sig;
}

/** The composer's opening text: what it starts with, and my signature between what I write and the quoted original. */
export function withSignature(text: string, signature: string | undefined): string {
  if (signature === undefined || signature.trim() === "") return text;
  return joinComposer({ ...splitComposer(text), signature });
}

/** Starts a body the server gave as its reading view (apps/api mail/readable.ts): shown as a reading column. */
export const READABLE_MARK = "<!--bundle:readable-->";

/** Starts a body the server gave as sent when the email also has a reading view (so the toggle shows). */
export const HAS_READABLE_MARK = "<!--bundle:has-readable-->";

/** How a message opens when nothing was chosen (Settings → Email): the email as sent, as Gmail shows it. */
export const DEFAULT_MAIL_BODY_VIEW: MailBodyView = "original";

export function isReadableBody(html: string): boolean {
  return html.startsWith(READABLE_MARK);
}

/** The email has a reading view: this body is it, or it's the original and says one exists. */
export function hasReadableView(html: string): boolean {
  return html.startsWith(READABLE_MARK) || html.startsWith(HAS_READABLE_MARK);
}

/** How messages open with these preferences (none saved, or saved before the choice existed: the default). */
export function mailBodyView(prefs: MailNotifyPrefs | undefined): MailBodyView {
  return prefs?.view ?? DEFAULT_MAIL_BODY_VIEW;
}

/** Gmail's search operators, read from a search box's text (`parseMailSearch`). */
export type MailSearch = {
  /** Plain words (full-text search over subject, body and people). */
  words: string[];
  from: string[];
  to: string[];
  subject: string[];
  /** `with:` — conversations with any of these people (sender or recipient); absent when not used. */
  with?: string[];
  hasAttachment?: true;
  unread?: boolean;
  starred?: true;
  /** `in:` — a folder or view; absent = everywhere but Spam and Trash (Gmail). */
  in?: "inbox" | "sent" | "archive" | "trash" | "spam" | "starred" | "snoozed" | "scheduled" | "anywhere";
  /** `label:` — a label's name (case-insensitive). */
  label?: string;
  /** `after:` / `before:` — YYYY-MM-DD (or with /), start of that day, UTC. */
  after?: number;
  before?: number;
};

const SEARCH_IN = ["inbox", "sent", "archive", "trash", "spam", "starred", "snoozed", "scheduled", "anywhere"] as const;

function searchDate(v: string): number | undefined {
  const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (m === null) return undefined;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(t) ? undefined : t;
}

/**
 * Splits a mail search into Gmail's operators (`from:ana`, `to:`, `subject:"proposta q4"`, `has:attachment`, Bundle's
 * `with:ana` (any of them, either side; Slack's `with:`),
 * `is:unread` / `is:read` / `is:starred`, `in:sent`, `label:financeiro`, `after:2026/10/01`, `before:`) and plain
 * words. Unknown or malformed operators stay words, so nothing typed is lost.
 */
export function parseMailSearch(q: string): MailSearch {
  const out: MailSearch = { words: [], from: [], to: [], subject: [] };
  for (const m of q.matchAll(/(\w+):"([^"]*)"|(\w+):(\S+)|"([^"]*)"|(\S+)/g)) {
    const key = (m[1] ?? m[3])?.toLowerCase();
    const value = (m[2] ?? m[4] ?? "").trim();
    if (key === undefined) {
      const w = (m[5] ?? m[6] ?? "").trim();
      if (w !== "") out.words.push(w);
      continue;
    }
    const lower = value.toLowerCase();
    const known =
      (key === "from" && value !== "" && out.from.push(lower) > 0) ||
      (key === "to" && value !== "" && out.to.push(lower) > 0) ||
      (key === "subject" && value !== "" && out.subject.push(lower) > 0) ||
      (key === "with" && value !== "" && (out.with ??= []).push(lower) > 0) ||
      (key === "has" && lower === "attachment" && (out.hasAttachment = true)) ||
      (key === "is" && (lower === "unread" || lower === "read") && ((out.unread = lower === "unread"), true)) ||
      (key === "is" && lower === "starred" && (out.starred = true)) ||
      (key === "in" && (SEARCH_IN as readonly string[]).includes(lower) && ((out.in = lower as MailSearch["in"]), true)) ||
      (key === "label" && value !== "" && ((out.label = lower), true)) ||
      (key === "after" && searchDate(value) !== undefined && ((out.after = searchDate(value)), true)) ||
      (key === "before" && searchDate(value) !== undefined && ((out.before = searchDate(value)), true));
    if (!known) out.words.push(m[0]);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Mail apps (IMAP / SMTP submission through the MX edge; docs/features/mail-clients.md)

/** Where mail apps connect: IMAP 993 (TLS), submission 465 (TLS) or 587 (STARTTLS); CalDAV on the web host. */
export const MAIL_CLIENT_HOSTS = { imap: "imap.bundle.direct", smtp: "smtp.bundle.direct", caldav: "app.bundle.direct", carddav: "app.bundle.direct" } as const;

function xmlText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * Thunderbird's autoconfig (config-v1.1.xml, also draft-ietf-mailmaint-autoconfig) for an address or a domain Bundle
 * hosts: IMAP 993 and submission 465 over TLS, with 587 STARTTLS as the other outgoing choice.
 */
export function mailAutoconfigXml(emailOrDomain: string, displayName: string): string {
  const domain = emailOrDomain.slice(emailOrDomain.lastIndexOf("@") + 1);
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<clientConfig version="1.1">',
    `  <emailProvider id="${xmlText(domain)}">`,
    `    <domain>${xmlText(domain)}</domain>`,
    `    <displayName>${xmlText(displayName)}</displayName>`,
    "    <displayShortName>Bundle</displayShortName>",
    '    <incomingServer type="imap">',
    `      <hostname>${MAIL_CLIENT_HOSTS.imap}</hostname>`,
    "      <port>993</port>",
    "      <socketType>SSL</socketType>",
    "      <authentication>password-cleartext</authentication>",
    "      <username>%EMAILADDRESS%</username>",
    "    </incomingServer>",
    '    <outgoingServer type="smtp">',
    `      <hostname>${MAIL_CLIENT_HOSTS.smtp}</hostname>`,
    "      <port>465</port>",
    "      <socketType>SSL</socketType>",
    "      <authentication>password-cleartext</authentication>",
    "      <username>%EMAILADDRESS%</username>",
    "    </outgoingServer>",
    '    <outgoingServer type="smtp">',
    `      <hostname>${MAIL_CLIENT_HOSTS.smtp}</hostname>`,
    "      <port>587</port>",
    "      <socketType>STARTTLS</socketType>",
    "      <authentication>password-cleartext</authentication>",
    "      <username>%EMAILADDRESS%</username>",
    "    </outgoingServer>",
    "  </emailProvider>",
    "</clientConfig>",
    "",
  ].join("\n");
}

/** Outlook's autodiscover (POX) answer for an address Bundle hosts. */
export function mailAutodiscoverXml(email: string): string {
  const proto = (type: "IMAP" | "SMTP", server: string, port: number) =>
    [
      "      <Protocol>",
      `        <Type>${type}</Type>`,
      `        <Server>${server}</Server>`,
      `        <Port>${port}</Port>`,
      `        <LoginName>${xmlText(email)}</LoginName>`,
      "        <SPA>off</SPA>",
      "        <SSL>on</SSL>",
      "        <AuthRequired>on</AuthRequired>",
      "      </Protocol>",
    ].join("\n");
  return [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<Autodiscover xmlns="http://schemas.microsoft.com/exchange/autodiscover/responseschema/2006">',
    '  <Response xmlns="http://schemas.microsoft.com/exchange/autodiscover/outlook/responseschema/2006a">',
    "    <Account>",
    "      <AccountType>email</AccountType>",
    "      <Action>settings</Action>",
    proto("IMAP", MAIL_CLIENT_HOSTS.imap, 993),
    proto("SMTP", MAIL_CLIENT_HOSTS.smtp, 465),
    "    </Account>",
    "  </Response>",
    "</Autodiscover>",
    "",
  ].join("\n");
}

/**
 * An Apple configuration profile (.mobileconfig) setting up iPhone / iPad / Mac Mail for each mailbox (IMAP + SMTP)
 * and Calendar (CalDAV), all with the person's app password. `ids`: one UUID per payload plus one for the profile
 * (accounts.length + 2 when there's a CalDAV account, else + 1); the caller makes them (random).
 */
/** One label of a profile's reverse-DNS identifier: lowercase letters, digits and hyphens. */
function identifierPart(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "") || "x";
}

export function mailMobileConfig(o: {
  name: string;
  accounts: { address: string; displayName: string }[];
  password: string;
  caldav?: { username: string };
  /** Contacts (CardDAV, the same app password). */
  carddav?: { username: string };
  ids: string[];
}): string {
  const s = (k: string, v: string) => `      <key>${k}</key>\n      <string>${xmlText(v)}</string>`;
  const i = (k: string, v: number) => `      <key>${k}</key>\n      <integer>${v}</integer>`;
  const b = (k: string, v: boolean) => `      <key>${k}</key>\n      <${v}/>`;
  const id = (n: number) => o.ids[n] ?? `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
  // Identifiers are stable per workspace and person (UUIDs aren't): installing a newer profile replaces the old one
  // and its accounts, instead of adding a second set next to them.
  const who = o.caldav?.username ?? o.carddav?.username ?? o.accounts[0]?.address ?? "";
  const key = (part: string) => `direct.bundle.${[o.name, who, part].map(identifierPart).join(".")}`;
  const mail = o.accounts.map((a, n) =>
    [
      "    <dict>",
      s("EmailAccountDescription", `${a.address} (Bundle)`),
      s("EmailAccountName", a.displayName),
      s("EmailAccountType", "EmailTypeIMAP"),
      s("EmailAddress", a.address),
      s("IncomingMailServerAuthentication", "EmailAuthPassword"),
      s("IncomingMailServerHostName", MAIL_CLIENT_HOSTS.imap),
      i("IncomingMailServerPortNumber", 993),
      b("IncomingMailServerUseSSL", true),
      s("IncomingMailServerUsername", a.address),
      s("IncomingPassword", o.password),
      s("OutgoingMailServerAuthentication", "EmailAuthPassword"),
      s("OutgoingMailServerHostName", MAIL_CLIENT_HOSTS.smtp),
      i("OutgoingMailServerPortNumber", 465),
      b("OutgoingMailServerUseSSL", true),
      s("OutgoingMailServerUsername", a.address),
      s("OutgoingPassword", o.password),
      s("PayloadDisplayName", a.address),
      s("PayloadIdentifier", key(`mail-${a.address}`)),
      s("PayloadType", "com.apple.mail.managed"),
      s("PayloadUUID", id(n)),
      i("PayloadVersion", 1),
      "    </dict>",
    ].join("\n"),
  );
  const cal =
    o.caldav === undefined
      ? []
      : [
          [
            "    <dict>",
            // macOS and iOS list it as its own account (beside the mail one, which offers Mail and Notes), so it's named
            // for what it is. The full principal URL: some versions don't resolve a bare path against the host.
            s("CalDAVAccountDescription", `Bundle Calendar (${o.name})`),
            s("CalDAVHostName", MAIL_CLIENT_HOSTS.caldav),
            s("CalDAVPassword", o.password),
            i("CalDAVPort", 443),
            s("CalDAVPrincipalURL", `https://${MAIL_CLIENT_HOSTS.caldav}/dav/`),
            b("CalDAVUseSSL", true),
            s("CalDAVUsername", o.caldav.username),
            s("PayloadDisplayName", "Calendar"),
            s("PayloadIdentifier", key("caldav")),
            s("PayloadType", "com.apple.caldav.account"),
            s("PayloadUUID", id(o.accounts.length)),
            i("PayloadVersion", 1),
            "    </dict>",
          ].join("\n"),
        ];
  const cardAt = o.accounts.length + cal.length;
  const card =
    o.carddav === undefined
      ? []
      : [
          [
            "    <dict>",
            s("CardDAVAccountDescription", `${o.name} (Bundle)`),
            s("CardDAVHostName", MAIL_CLIENT_HOSTS.carddav),
            s("CardDAVPassword", o.password),
            i("CardDAVPort", 443),
            s("CardDAVPrincipalURL", `https://${MAIL_CLIENT_HOSTS.carddav}/dav/`),
            b("CardDAVUseSSL", true),
            s("CardDAVUsername", o.carddav.username),
            s("PayloadDisplayName", "Contacts"),
            s("PayloadIdentifier", key("carddav")),
            s("PayloadType", "com.apple.carddav.account"),
            s("PayloadUUID", id(cardAt)),
            i("PayloadVersion", 1),
            "    </dict>",
          ].join("\n"),
        ];
  const top = id(cardAt + card.length);
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">',
    '<plist version="1.0">',
    "<dict>",
    "  <key>PayloadContent</key>",
    "  <array>",
    ...mail,
    ...cal,
    ...card,
    "  </array>",
    "  <key>PayloadDisplayName</key>",
    `  <string>${xmlText(`Bundle — ${o.name}`)}</string>`,
    "  <key>PayloadIdentifier</key>",
    `  <string>${xmlText(key("profile"))}</string>`,
    "  <key>PayloadRemovalDisallowed</key>",
    "  <false/>",
    "  <key>PayloadType</key>",
    "  <string>Configuration</string>",
    "  <key>PayloadUUID</key>",
    `  <string>${top}</string>`,
    "  <key>PayloadVersion</key>",
    "  <integer>1</integer>",
    "</dict>",
    "</plist>",
    "",
  ].join("\n");
}

// ---------- Verification codes ----------

/** What's shown of a thread's code (contracts MailThread.verificationCode). `code` null: no code to show, only the warning. */
export type MailCodeView = { code: string | null; copyText: string; spam: boolean; expiresAt: number };

/** What Copy puts on the clipboard: sites take "482 913" typed without its space. */
export function mailCodeCopyText(code: string): string {
  return code.replace(/\s+/g, "");
}

/** The thread's code while it lasts, else null. */
export function mailCodeView(thread: Pick<MailThread, "folder" | "verificationCode">, now: number): MailCodeView | null {
  const c = thread.verificationCode;
  if (c === undefined || c === null || c.expiresAt <= now) return null;
  return { code: c.code, copyText: c.code === null ? "" : mailCodeCopyText(c.code), spam: thread.folder === "spam", expiresAt: c.expiresAt };
}

// ---------------------------------------------------------------------------------------------------------------
// One list across my mailboxes (docs/features/mail.md, "All my mailboxes")

/** New mail starts here: the mailbox in view, else my own, else the first. */
export function homeMailboxId(mailboxes: Pick<Mailbox, "id" | "kind">[], filtered: string | undefined): string | undefined {
  if (filtered !== undefined && mailboxes.some((m) => m.id === filtered)) return filtered;
  return (mailboxes.find((m) => m.kind === "personal") ?? mailboxes[0])?.id;
}

/** The groups my mailboxes belong to, by address (the Mailbox menu's Groups: each means `to:` it). */
export function mailboxGroups(mailboxes: Pick<Mailbox, "sendAs">[]): string[] {
  const out = new Set<string>();
  for (const m of mailboxes) for (const a of m.sendAs ?? []) if (a.group === true) out.add(a.address.toLowerCase());
  return [...out].toSorted();
}

/** My mailboxes as the menu lists them: my own first, then by address. */
export function mailboxMenuOrder<T extends Pick<Mailbox, "kind" | "address">>(mailboxes: T[]): T[] {
  return mailboxes.toSorted((a, b) => (a.kind === b.kind ? (a.address < b.address ? -1 : a.address > b.address ? 1 : 0) : a.kind === "personal" ? -1 : 1));
}

/** The short name a pill or chip shows: `financeiro@` for `financeiro@bigsmall.xyz`. */
export function mailboxShortName(address: string): string {
  const at = address.lastIndexOf("@");
  return at < 0 ? address : address.slice(0, at + 1);
}

/** Inbox's unread beside the folder: the mailbox in view's, or all of them added up. */
export function mailInboxUnread(mailboxes: Pick<Mailbox, "id" | "unread">[], filtered: string | undefined): number {
  return mailboxes.reduce((n, m) => (filtered === undefined || m.id === filtered ? n + m.unread : n), 0);
}

export type MailRowPill = { label: string; tone: "shared" | "group" };

/**
 * The pills on a row of the unified list: the group it came through, then each mailbox it's in. A row in my own mailbox
 * alone has none; a collapsed row names every mailbox, mine too. `show`: false when one mailbox fills the list.
 */
export function mailRowPills(thread: Pick<MailThread, "mailboxId" | "via" | "copies">, mailboxes: Pick<Mailbox, "id" | "address" | "kind">[], show: boolean): MailRowPill[] {
  if (!show) return [];
  const box = (id: string) => mailboxes.find((m) => m.id === id);
  const copies = (thread.copies ?? []).flatMap((c) => box(c.mailboxId) ?? []);
  const here = box(thread.mailboxId);
  const inBoxes = copies.length > 0 ? [...(here === undefined ? [] : [here]), ...copies] : here?.kind === "shared" ? [here] : [];
  const pills: MailRowPill[] = thread.via === undefined ? [] : [{ label: mailboxShortName(thread.via), tone: "group" }];
  for (const m of inBoxes) pills.push({ label: mailboxShortName(m.address), tone: "shared" });
  return pills;
}

export type MailLabelRow = { id: string; ids: string[]; name: string; color: string };

/** Labels with one name (case aside) are one row across mailboxes; `id` is the first's, which the list route takes. */
export function mergedMailLabels(labels: MailLabel[]): MailLabelRow[] {
  const rows = new Map<string, MailLabelRow>();
  for (const l of labels) {
    const key = l.name.toLowerCase();
    const row = rows.get(key);
    if (row === undefined) rows.set(key, { id: l.id, ids: [l.id], name: l.name, color: l.color });
    else row.ids.push(l.id);
  }
  return [...rows.values()];
}

/** The ids a label's row stands for (none when it isn't known yet: just itself). */
export function mailLabelIds(labels: MailLabel[], id: string): string[] {
  return mergedMailLabels(labels).find((r) => r.ids.includes(id))?.ids ?? [id];
}
